import { Signal } from '@crown/signal';
import { InstructionError } from '../ir/values';
import { type Disposer, type Teardown, disposeAll } from './bindings';
import { checkFocusBeforeRemoval } from './dev';
import { type FocusPlace, focusPlaceAmong, restoreFocus } from './focus';

/**
 * The items of one list as its rows read them. A key keeps its row; when a new
 * object arrives under a kept key the row keeps its nodes and takes the new
 * item, and the list's one version signal tells every binding that read an
 * item to read it again.
 *
 * A list starts untracked, so a list whose objects never change pays nothing:
 * its rows' reads of their item link to no signal, and a binding that reads
 * only its item is a constant. The first new object under a kept key rebuilds
 * that row and turns tracking on; from then on rows update in place.
 */
export class ListItems {
  readonly version = new Signal.State(0);
  tracking = false;

  /** `readsItems` is false for rows written without the item reader: those keep their row by key alone. */
  constructor(readonly readsItems = true) {}
}

/**
 * A row's position and item, each read through a reader made only when a row
 * template asks for it: most rows never ask for their index, and one object
 * per row carries both.
 */
export class RowIndex<T = unknown> {
  private signal: Signal.State<number> | undefined;

  private reader: (() => number) | undefined;

  private itemReader: (() => T) | undefined;

  constructor(
    private value: number,
    private itemValue: T,
    private readonly list: ListItems,
  ) {}

  /** The index as a reader, made only for a row template that takes one. */
  get read(): () => number {
    return (this.reader ??= () => (this.signal ??= new Signal.State(this.value)).get());
  }

  set(value: number): void {
    if (value === this.value) return;
    this.value = value;
    this.signal?.set(value);
  }

  /** The item as a reader: through the list's version once the list is tracked. */
  get readItem(): () => T {
    return (this.itemReader ??= () => {
      if (this.list.tracking) this.list.version.get();
      return this.itemValue;
    });
  }

  /** The item the row shows now. */
  get item(): T {
    return this.itemValue;
  }

  /** Takes a new item; whether it differs, for the list to bump its version once. */
  setItem(value: T): boolean {

    if (value === this.itemValue) return false;
    this.itemValue = value;
    return true;
  }
}

/**
 * One live row: the first and last of its nodes, its disposer and its index.
 * A row moves and leaves as the range between its first and last node, so
 * content its own markers have since gained between them goes with it.
 */
export interface RowRecord {
  readonly first: Node | null;
  readonly last: Node | null;
  readonly disposers: Teardown[];
  /** The row's position and item; a kept row takes over a new object that arrives under its key. */
  readonly index: RowIndex;
}

export interface CreatedRow {
  readonly nodes: Node[];
  readonly disposers: Teardown[];
}

export function rowRecord(created: CreatedRow, index: RowIndex): RowRecord {
  const { nodes } = created;
  return { first: nodes[0] ?? null, last: nodes[nodes.length - 1] ?? null, disposers: created.disposers, index };
}

/** Moves a row's range before `before`, or takes it off the page. */
function moveRange(parent: Node, row: RowRecord, before: Node | null, remove: boolean): void {
  let node = row.first;
  while (node) {
    const next: Node | null = node === row.last ? null : node.nextSibling;
    if (remove) parent.removeChild(node);
    else parent.insertBefore(node, before);
    node = next;
  }
}

/**
 * The positions in `sources` that form a longest strictly increasing run, as a
 * set of indexes into `sources`. A source of -1 (a new row) never joins it.
 *
 * Rows on this run are already in order relative to each other, so only the
 * rest move: swapping two rows of a thousand moves two, not the 997 between.
 */
function longestIncreasing(sources: Int32Array): Uint8Array {
  const n = sources.length;
  const previous = new Int32Array(n);
  const tails: number[] = [];
  // Typed arrays read in range here, so the fallbacks never apply; they are
  // what the index type asks for.
  for (let i = 0; i < n; i++) {
    const value = sources[i] ?? -1;
    if (value < 0) continue;
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if ((sources[tails[mid] ?? 0] ?? -1) < value) low = mid + 1;
      else high = mid;
    }
    previous[i] = low > 0 ? (tails[low - 1] ?? -1) : -1;
    tails[low] = i;
  }
  const keep = new Uint8Array(n);
  let at = tails.length > 0 ? (tails[tails.length - 1] ?? -1) : -1;
  while (at >= 0) {
    keep[at] = 1;
    at = previous[at] ?? -1;
  }
  return keep;
}

/** A row's nodes, first to last. */
function rowNodes(row: RowRecord): Node[] {
  const nodes: Node[] = [];
  for (let node = row.first; node; node = node === row.last ? null : node.nextSibling) nodes.push(node);
  return nodes;
}

/** `focusKept` says focus moves into the row built in its place, so its removal is not a mistake. */
function removeRow(parent: Node, row: RowRecord, focusKept = false): void {
  if (row.first && !focusKept) checkFocusBeforeRemoval(row.first);
  disposeAll(row.disposers);
  moveRange(parent, row, null, true);
}

/**
 * The nodes around a list when they are only markers — a template hole the
 * list fills, say — so the parent holds nothing but the list and can be
 * emptied in one write. Null when anything else shares the parent.
 */
function markersAround(start: Node, end: Node): { before: Node[]; after: Node[] } | null {
  const before: Node[] = [];
  for (let node = start.previousSibling; node; node = node.previousSibling) {
    if (node.nodeType !== 8 || before.length === 4) return null;
    before.unshift(node);
  }
  const after: Node[] = [];
  for (let node = end.nextSibling; node; node = node.nextSibling) {
    if (node.nodeType !== 8 || after.length === 4) return null;
    after.push(node);
  }
  return { before, after };
}

/** `list[i]` for an index already checked against the length. */
function itemAt<T>(list: readonly T[], i: number): T {
  // SAFETY: every caller loops `i` below `list.length`, and an item may itself be undefined.
  return list[i] as T;
}

/** Whether the rows on the page are, in order, the first of `keys`, with more after them. */
/**
 * Rows taken out of one place and nothing else changed - the same items, the
 * same objects, before the gap and after it - as deleting a row does. Found by
 * comparing items, with no key worked out for a row that stays before the gap
 * and none put in a set: what a thousand-row list pays to lose one row is its
 * comparisons. A list keyed by position is not this case - the rows after the
 * gap would take other keys - and neither is anything else; both take the
 * general path.
 */
function removedFromOnePlace<T>(parent: Node, rows: Map<string, RowRecord>, items: readonly T[], key: (item: T, index: number) => string): boolean {
  const m = rows.size;
  const n = items.length;
  if (n >= m || n === 0) return false;
  const records = Array.from(rows.values());
  const gone = m - n;
  let pre = 0;
  while (pre < n && records[pre]?.index.item === items[pre]) pre++;
  for (let i = pre; i < n; i++) if (records[i + gone]?.index.item !== items[i]) return false;
  // Each row after the gap keeps its key where it now stands.
  for (let i = pre; i < n; i++) {
    const item = itemAt(items, i);
    if (key(item, i) !== key(item, i + gone)) return false;
  }
  const leaving: [string, RowRecord][] = [];
  for (let i = pre; i < pre + gone; i++) {
    const record = records[i];
    if (!record) return false;
    // SAFETY: a row's item is one of this list's items, which are T.
    const k = key(record.index.item as T, i);
    if (rows.get(k) !== record) return false;
    leaving.push([k, record]);
  }
  for (const [k, record] of leaving) {
    removeRow(parent, record);
    rows.delete(k);
  }
  for (let i = pre; i < n; i++) records[i + gone]?.index.set(i);
  return true;
}

function appendsTo(rows: Map<string, RowRecord>, keys: readonly string[]): boolean {
  if (keys.length <= rows.size) return false;
  let i = 0;
  for (const k of rows.keys()) {
    if (keys[i] !== k) return false;
    i++;
  }
  return true;
}

/** Keyed reconciliation between the `c:each` markers. */
export function reconcileRows<T>(
  start: Comment,
  end: Comment,
  rows: Map<string, RowRecord>,
  items: readonly T[],
  key: (item: T, index: number) => string,
  create: (item: T, index: RowIndex) => CreatedRow,
  list: ListItems,
): void {
  const parent = end.parentNode;
  if (!parent) throw new InstructionError('each() markers must be attached before rows reconcile');

  const n = items.length;

  // Nothing on the page yet: build every row off the page and insert once.
  // The row map is the duplicate check, so no second set of keys is built.
  if (rows.size === 0) {
    if (n === 0) return;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < n; i++) {
      const item = itemAt(items, i);
      const k = key(item, i);
      if (rows.has(k)) throw new InstructionError(`each() produced the key "${k}" twice`);
      const index = new RowIndex(i, item, list);
      const created = create(item, index);
      rows.set(k, rowRecord(created, index));
      for (const node of created.nodes) fragment.appendChild(node);
    }
    parent.insertBefore(fragment, end);
    return;
  }

  if (removedFromOnePlace(parent, rows, items, key)) return;

  const keys: string[] = [];
  const wanted = new Set<string>();
  for (let i = 0; i < n; i++) {
    const k = key(itemAt(items, i), i);
    if (wanted.has(k)) throw new InstructionError(`each() produced the key "${k}" twice`);
    wanted.add(k);
    keys.push(k);
  }

  // A new object under a kept key. A tracked list updates the row in place;
  // an untracked one rebuilds it, as nothing in it reads the item through a
  // signal, and starts tracking so the rebuilt rows do.
  const leaving = new Set<string>();
  if (list.readsItems && !list.tracking) {
    for (let i = 0; i < n; i++) {
      const k = keys[i] ?? '';
      const existing = rows.get(k);
      if (existing && existing.index.item !== itemAt(items, i)) leaving.add(k);
    }
    if (leaving.size > 0) list.tracking = true;
  }
  const keeps = (k: string): boolean => wanted.has(k) && !leaving.has(k);
  // A row built again keeps focus where it was in the row it replaces.
  let refocus: { readonly key: string; readonly place: FocusPlace } | null = null;
  for (const k of leaving) {
    const row = rows.get(k);
    const place = row ? focusPlaceAmong(rowNodes(row)) : null;
    if (place) refocus = { key: k, place };
  }
  const restore = (): void => {
    const row = refocus ? rows.get(refocus.key) : undefined;
    if (refocus && row) restoreFocus(refocus.place, rowNodes(row));
  };

  // Rows that leave. When none stay and the markers are the parent's only
  // children, the whole list goes in one write instead of one per node.
  let kept = 0;
  for (const k of rows.keys()) if (keeps(k)) kept++;
  if (kept === 0 && rows.size > 0) {
    for (const row of rows.values()) disposeAll(row.disposers);
    const outside = markersAround(start, end);
    if (outside) {
      parent.textContent = '';
      for (const node of outside.before) parent.appendChild(node);
      parent.appendChild(start);
      parent.appendChild(end);
      for (const node of outside.after) parent.appendChild(node);
    } else {
      for (const row of rows.values()) moveRange(parent, row, null, true);
    }
    rows.clear();
  } else if (kept < rows.size) {
    for (const [k, row] of rows) {
      if (keeps(k)) continue;
      removeRow(parent, row, refocus?.key === k);
      rows.delete(k);
    }
  }

  // Every row left: build the new ones off the page and insert once.
  if (rows.size === 0) {
    if (n === 0) return;
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < n; i++) {
      const item = itemAt(items, i);
      const index = new RowIndex(i, item, list);
      const created = create(item, index);
      rows.set(keys[i] ?? '', rowRecord(created, index));
      for (const node of created.nodes) fragment.appendChild(node);
    }
    parent.insertBefore(fragment, end);
    restore();
    return;
  }

  // Every row stays where it was and new ones follow: an append. The kept rows
  // need only their items refreshed, and the new ones go in as one insert.
  if (appendsTo(rows, keys)) {
    let replacedItem = false;
    let i = 0;
    for (const row of rows.values()) {
      if (row.index.setItem(itemAt(items, i))) replacedItem = true;
      i++;
    }
    if (replacedItem) list.version.set(list.version.get() + 1);
    const fragment = document.createDocumentFragment();
    for (; i < n; i++) {
      const item = itemAt(items, i);
      const index = new RowIndex(i, item, list);
      const created = create(item, index);
      rows.set(keys[i] ?? '', rowRecord(created, index));
      for (const node of created.nodes) fragment.appendChild(node);
    }
    parent.insertBefore(fragment, end);
    restore();
    return;
  }

  // `rows` iterates in page order: it is rebuilt in the wanted order below.
  const oldPosition = new Map<string, number>();
  let position = 0;
  for (const k of rows.keys()) oldPosition.set(k, position++);

  const sources = new Int32Array(n);
  const records: RowRecord[] = [];
  // A new row's nodes are not siblings yet, so they go in as a list.
  const fresh = new Map<number, Node[]>();
  let replaced = false;
  items.forEach((item, i) => {
    const k = keys[i] ?? '';
    const existing = rows.get(k);
    if (existing) {
      sources[i] = oldPosition.get(k) ?? -1;
      existing.index.set(i);
      if (existing.index.setItem(item)) replaced = true;
      records.push(existing);
    } else {
      sources[i] = -1;
      const index = new RowIndex(i, item, list);
      const created = create(item, index);
      fresh.set(i, created.nodes);
      records.push(rowRecord(created, index));
    }
  });
  // One bump for every replaced item: each binding that read one reads again.
  if (replaced) list.version.set(list.version.get() + 1);

  const stays = longestIncreasing(sources);
  let next: Node = end;
  // New rows that sit next to each other are gathered off the page and go in
  // as one insert: appending a thousand rows is one write, not a thousand.
  let batch: DocumentFragment | null = null;
  const flush = (): void => {
    if (!batch) return;
    const head = batch.firstChild;
    parent.insertBefore(batch, next);
    if (head) next = head;
    batch = null;
  };
  for (let i = n - 1; i >= 0; i--) {
    const record = records[i];
    if (!record) continue;
    const nodes = fresh.get(i);
    if (nodes) {
      batch ??= document.createDocumentFragment();
      const head = batch.firstChild;
      for (const node of nodes) batch.insertBefore(node, head);
      continue;
    }
    flush();
    if (!stays[i]) moveRange(parent, record, next, false);
    next = record.first ?? next;
  }
  flush();

  rows.clear();
  records.forEach((record, i) => rows.set(keys[i] ?? '', record));
  restore();
}
